/**
 * PromptDialog — shadcn-based replacement for window.prompt().
 * Tablet-friendly. Supports an optional <select> dropdown (preset reasons)
 * AND/OR a free-text textarea for nurses' "other" reasons.
 *
 * Usage:
 *   const [reason, setReason] = usePromptDialog();
 *   const reason = await reason.open({ title: 'Missed dose?', presets: ['Patient sleeping', 'NPO', 'Not available'] });
 *   if (reason) ... // user clicked confirm
 */
import { useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

/**
 * Headless hook — returns [renderNode, open()].
 * `open(options)` returns a Promise<string|null> resolving with the entered/selected text.
 */
export const usePromptDialog = () => {
  const [state, setState] = useState({ open: false, title: '', label: '', presets: [], placeholder: '' });
  const resolverRef = useRef(null);
  const [selectVal, setSelectVal] = useState('');
  const [textVal, setTextVal] = useState('');

  const open = useCallback((opts = {}) => {
    setSelectVal('');
    setTextVal('');
    setState({
      open: true,
      title: opts.title || 'Add note',
      label: opts.label || 'Reason',
      presets: opts.presets || [],
      placeholder: opts.placeholder || 'Type a note…',
      confirmLabel: opts.confirmLabel || 'Save',
      destructive: opts.destructive || false,
      required: opts.required ?? true,
    });
    return new Promise(resolve => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (value) => {
    setState(s => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  };

  const handleConfirm = () => {
    // Prefer preset if chosen, append free-text if both, else free-text.
    let val = selectVal;
    if (textVal.trim()) {
      val = val ? `${val} — ${textVal.trim()}` : textVal.trim();
    }
    if (state.required && !val) return;
    close(val || null);
  };

  const node = (
    <Dialog open={state.open} onOpenChange={(o) => { if (!o) close(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{state.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {state.presets?.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Common reasons</Label>
              <div className="flex flex-wrap gap-2">
                {state.presets.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setSelectVal(p)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                      selectVal === p
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`prompt-preset-${p.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{state.presets?.length ? 'Additional notes (optional)' : state.label}</Label>
            <Textarea
              rows={3}
              value={textVal}
              onChange={(e) => setTextVal(e.target.value)}
              placeholder={state.placeholder}
              data-testid="prompt-textarea"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(null)} className="h-11 px-5" data-testid="prompt-cancel">Cancel</Button>
          <Button
            onClick={handleConfirm}
            className={`h-11 px-5 ${state.destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            data-testid="prompt-confirm"
          >
            {state.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return [node, open];
};
